/**
 * <XtsMoverTools> — Renders the NCT mover-tool carriers.
 *
 * Tools live in the mover-local frame: +X = travel direction, +Z = up.
 * Each tool's own GLB origin-correction (from <toolType>.tool.meta.json) is
 * applied INSIDE the offset specified by the user; the tool's offsetMm /
 * rotationDegEuler then position the corrected GLB on the mover.
 *
 * Custom tools accept a `customGlbUrl` instead of a predefined `toolType`,
 * with their own `customOriginCorrection`.
 */

import React from 'react';
import { CustomAssetMesh } from './CustomAssetMesh.js';
import { useToolSidecar } from './useToolSidecar.js';
import { resolveToolGlbUrl } from '../../assets/AssetManifest.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import {
  DEFAULT_ORIGIN_CORRECTION,
  type OriginCorrection,
} from '../../assets/SidecarLoader.js';
import type {
  AssetManifest,
  MoverToolConfig,
  MoverToolType3D,
} from '../../types.js';

const DEG2RAD = Math.PI / 180;

interface XtsMoverToolsProps {
  tools: MoverToolConfig[];
  assetsBaseUrl: string;
  assetManifest?: AssetManifest;
  /** Live calibration overrides keyed by toolType. */
  toolOverrides?: Partial<Record<MoverToolType3D, OriginCorrection>>;
}

export const XtsMoverTools: React.FC<XtsMoverToolsProps> = ({
  tools,
  assetsBaseUrl,
  assetManifest,
  toolOverrides,
}) => {
  return (
    <group name="xts-mover-tools">
      {tools.map((tool, i) => (
        <ToolInstance
          key={tool.id ?? `${tool.toolType}-${i}`}
          tool={tool}
          assetsBaseUrl={assetsBaseUrl}
          assetManifest={assetManifest}
          override={
            tool.toolType !== 'Custom'
              ? toolOverrides?.[tool.toolType]
              : undefined
          }
        />
      ))}
    </group>
  );
};

const ToolInstance: React.FC<{
  tool: MoverToolConfig;
  assetsBaseUrl: string;
  assetManifest?: AssetManifest;
  override?: OriginCorrection;
}> = ({ tool, assetsBaseUrl, assetManifest, override }) => {
  const url =
    tool.toolType === 'Custom'
      ? tool.customGlbUrl ?? ''
      : composeAssetUrl(
          assetsBaseUrl,
          resolveToolGlbUrl(tool.toolType, assetManifest?.tools),
        );
  // Default placement: 30 mm above the magnet plate so the tool sits
  // visibly on top of the mover regardless of GLB origin choice.
  const offset = tool.offsetMm ?? [0, 0, 30];

  // Predefined tools pull origin-correction from <toolType>.tool.meta.json;
  // Custom tools use the user-supplied customOriginCorrection. Live override
  // beats both for the calibration tool.
  const sidecar = useToolSidecar(
    tool.toolType !== 'Custom' ? tool.toolType : ('AT8200_1000_0100' as const),
    assetsBaseUrl,
  );
  const correction: OriginCorrection =
    override ??
    (tool.toolType === 'Custom'
      ? tool.customOriginCorrection ?? DEFAULT_ORIGIN_CORRECTION
      : sidecar.correction);

  return (
    <group
      position={offset}
      rotation={[
        (tool.rotationDegEuler?.[0] ?? 0) * DEG2RAD,
        (tool.rotationDegEuler?.[1] ?? 0) * DEG2RAD,
        (tool.rotationDegEuler?.[2] ?? 0) * DEG2RAD,
        'XYZ',
      ]}
    >
      <CustomAssetMesh
        glbUrl={url}
        positionMm={correction.translateMm}
        rotationDegEuler={correction.rotationDegEuler}
        opacity={tool.opacity ?? 1}
        visible={tool.visible !== false}
        placeholderSizeMm={[120, 60, 40]}
      />
    </group>
  );
};
